import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
  Building2,
  Users,
  UserPlus,
  Mail,
  Shield,
  Briefcase,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
  Crown,
} from "lucide-react";

const PERMISSION_LABELS = {
  company_admin: { name: "Company Admin", color: "bg-purple-100 text-purple-700" },
  full_access: { name: "Full Access", color: "bg-blue-100 text-blue-700" },
  standard: { name: "Standard", color: "bg-green-100 text-green-700" },
  limited: { name: "Limited", color: "bg-gray-100 text-gray-700" },
};

const ClientCompany = () => {
  const { token, API, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCompanyEditDialog, setShowCompanyEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingCompany, setUpdatingCompany] = useState(false);
  
  // Company profile edit form state
  const [companyForm, setCompanyForm] = useState({
    name: "",
    industry: "",
    website: "",
    phone: "",
    address: "",
  });
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    permission_level: "standard",
    assigned_projects: [],
  });

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      // Get company info
      const companyRes = await fetch(`${API}/companies/my-company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (companyRes.ok) {
        const data = await companyRes.json();
        setCompanyData(data);
        setIsCompanyAdmin(data.is_company_admin);
        
        // If company admin, fetch users and projects
        if (data.is_company_admin && data.company) {
          const usersRes = await fetch(`${API}/companies/my-company/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            setUsers(usersData.users || []);
            setProjects(usersData.projects || []);
          }
        }
      }
    } catch (error) {
      toast.error("Failed to load company data");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setInviting(true);
    try {
      const params = new URLSearchParams({
        email: inviteForm.email,
        name: inviteForm.name,
        permission_level: inviteForm.permission_level,
      });
      
      // Add assigned projects if any
      if (inviteForm.assigned_projects.length > 0) {
        inviteForm.assigned_projects.forEach(p => params.append("assigned_projects", p));
      }
      
      const response = await fetch(`${API}/companies/my-company/invite?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowInviteDialog(false);
        setInviteForm({
          email: "",
          name: "",
          permission_level: "standard",
          assigned_projects: [],
        });
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to invite user");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setUpdating(true);
    try {
      // Always send assigned_projects, even if empty (to clear assignments)
      const assignedProjects = selectedUser.assigned_projects || [];
      
      // Build request body
      const requestBody = {
        permission_level: selectedUser.permission_level,
        assigned_projects: assignedProjects,
      };
      
      const response = await fetch(
        `${API}/companies/my-company/users/${selectedUser.user_id}`,
        {
          method: "PUT",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      
      if (response.ok) {
        toast.success("User updated successfully");
        setShowEditDialog(false);
        setSelectedUser(null);
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const openCompanyEditDialog = () => {
    if (companyData?.company) {
      setCompanyForm({
        name: companyData.company.name || "",
        industry: companyData.company.industry || "",
        website: companyData.company.website || "",
        phone: companyData.company.phone || "",
        address: companyData.company.address || "",
      });
    }
    setShowCompanyEditDialog(true);
  };

  const handleUpdateCompany = async () => {
    setUpdatingCompany(true);
    try {
      const params = new URLSearchParams();
      if (companyForm.name) params.append("name", companyForm.name);
      if (companyForm.industry !== undefined) params.append("industry", companyForm.industry);
      if (companyForm.website !== undefined) params.append("website", companyForm.website);
      if (companyForm.phone !== undefined) params.append("phone", companyForm.phone);
      if (companyForm.address !== undefined) params.append("address", companyForm.address);

      const response = await fetch(`${API}/companies/my-company?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Company profile updated successfully");
        setShowCompanyEditDialog(false);
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update company profile");
      }
    } catch (error) {
      toast.error("Failed to update company profile");
    } finally {
      setUpdatingCompany(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!confirm("Are you sure you want to remove this user from the company?")) return;
    
    try {
      const response = await fetch(`${API}/companies/my-company/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("User removed from company");
        fetchCompanyData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove user");
      }
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };

  const openEditDialog = (u) => {
    setSelectedUser({
      ...u,
      assigned_projects: u.assigned_projects || [],
    });
    setShowEditDialog(true);
  };

  const toggleProjectAssignment = (projectId, isEdit = false) => {
    if (isEdit && selectedUser) {
      const current = selectedUser.assigned_projects || [];
      const updated = current.includes(projectId)
        ? current.filter(p => p !== projectId)
        : [...current, projectId];
      setSelectedUser({ ...selectedUser, assigned_projects: updated });
    } else {
      const current = inviteForm.assigned_projects || [];
      const updated = current.includes(projectId)
        ? current.filter(p => p !== projectId)
        : [...current, projectId];
      setInviteForm({ ...inviteForm, assigned_projects: updated });
    }
  };

  if (loading) {
    return (
      <ClientLayout activeNav="company">
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-rnb-teal/30 border-t-rnb-teal rounded-full animate-spin"></div>
        </div>
      </ClientLayout>
    );
  }

  if (!companyData?.company) {
    return (
      <ClientLayout activeNav="company">
        <div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-card p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-rnb-navy mb-1">No Company Assigned</h3>
            <p className="text-sm text-gray-400">You are not currently assigned to a company. Contact your administrator for assistance.</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!isCompanyAdmin) {
    return (
      <ClientLayout activeNav="company">
        <div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-card p-12 text-center">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-rnb-navy mb-1">Access Restricted</h3>
            <p className="text-sm text-gray-400 mb-2">Only company administrators can manage team members.</p>
            <p className="text-xs text-gray-400">You are a member of <strong>{companyData.company.name}</strong></p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeNav="company">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy" data-testid="company-title">Company Management</h1>
            <p className="text-sm text-gray-400 mt-0.5">Manage your team members and their project access.</p>
          </div>
        </div>

        {/* Company Info Card */}
        <Card data-testid="company-info-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-rnb-blue" />
                  {companyData.company.name}
                </CardTitle>
                <CardDescription>
                  {companyData.company.industry && `${companyData.company.industry} • `}
                  {users.length} team member{users.length !== 1 ? "s" : ""}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={openCompanyEditDialog}
                data-testid="edit-company-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {companyData.company.website && (
                <div>
                  <span className="text-rnb-gray">Website:</span>{" "}
                  <a href={companyData.company.website.startsWith('http') ? companyData.company.website : `https://${companyData.company.website}`} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-rnb-blue hover:underline">
                    {companyData.company.website}
                  </a>
                </div>
              )}
              {companyData.company.phone && (
                <div>
                  <span className="text-rnb-gray">Phone:</span>{" "}
                  <span className="text-rnb-navy">{companyData.company.phone}</span>
                </div>
              )}
              {companyData.company.address && (
                <div className="md:col-span-2">
                  <span className="text-rnb-gray">Address:</span>{" "}
                  <span className="text-rnb-navy">{companyData.company.address}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team Members Card */}
        <Card data-testid="team-members-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-rnb-blue" />
                  Team Members
                </CardTitle>
                <CardDescription>
                  Invite new members and manage their access to projects.
                </CardDescription>
              </div>
              <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="invite-user-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>
                      Send an invitation to join your company on the client portal.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-name">Name *</Label>
                      <Input
                        id="invite-name"
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        placeholder="John Smith"
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-email">Email *</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        placeholder="john@example.com"
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-permission">Permission Level</Label>
                      <Select
                        value={inviteForm.permission_level}
                        onValueChange={(v) => setInviteForm({ ...inviteForm, permission_level: v })}
                      >
                        <SelectTrigger data-testid="invite-permission-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_access">Full Access - View all company data</SelectItem>
                          <SelectItem value="standard">Standard - View projects & complete tasks</SelectItem>
                          <SelectItem value="limited">Limited - Only assigned projects</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {projects.length > 0 && (
                      <div className="space-y-2">
                        <Label>Assign to Projects</Label>
                        <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                          {projects.map((project) => (
                            <div key={project.project_id} className="flex items-center gap-2">
                              <Checkbox
                                id={`invite-project-${project.project_id}`}
                                checked={inviteForm.assigned_projects.includes(project.project_id)}
                                onCheckedChange={() => toggleProjectAssignment(project.project_id)}
                              />
                              <label
                                htmlFor={`invite-project-${project.project_id}`}
                                className="text-sm cursor-pointer"
                              >
                                {project.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleInviteUser} disabled={inviting} data-testid="send-invite-btn">
                      {inviting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-rnb-gray">No team members yet. Invite your first team member!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.user_id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      u.is_current_user ? "bg-rnb-light border-rnb-blue/30" : "bg-slate-50"
                    }`}
                    data-testid={`user-${u.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-rnb-blue/10 flex items-center justify-center">
                        {u.is_company_admin ? (
                          <Crown className="h-5 w-5 text-rnb-blue" />
                        ) : (
                          <span className="text-rnb-blue font-medium">
                            {u.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-rnb-navy">{u.name}</p>
                          {u.is_current_user && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                        <p className="text-sm text-rnb-gray">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={PERMISSION_LABELS[u.permission_level]?.color || "bg-gray-100"}>
                        {PERMISSION_LABELS[u.permission_level]?.name || u.permission_level}
                      </Badge>
                      {u.assigned_projects?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {u.assigned_projects.length} project{u.assigned_projects.length !== 1 ? "s" : ""}
                        </Badge>
                      )}
                      {!u.is_company_admin && !u.is_current_user && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(u)}
                            title="Manage project access"
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Briefcase className="h-4 w-4 mr-1" />
                            <span className="text-xs">Projects</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUser(u.user_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Remove user from company"
                            data-testid={`remove-user-${u.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Manage Project Access</DialogTitle>
              <DialogDescription>
                Control which projects {selectedUser?.name} can see and access. Uncheck a project to remove access.
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Permission Level</Label>
                  <Select
                    value={selectedUser.permission_level}
                    onValueChange={(v) => setSelectedUser({ ...selectedUser, permission_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_access">Full Access - View all company data</SelectItem>
                      <SelectItem value="standard">Standard - View projects & complete tasks</SelectItem>
                      <SelectItem value="limited">Limited - Only assigned projects</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    "Limited" users will ONLY see projects checked below.
                  </p>
                </div>
                {projects.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Assigned Projects</Label>
                      <span className="text-xs text-gray-500">
                        {selectedUser.assigned_projects?.length || 0} of {projects.length} selected
                      </span>
                    </div>
                    <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                      {projects.map((project) => {
                        const isAssigned = selectedUser.assigned_projects?.includes(project.project_id);
                        return (
                          <div 
                            key={project.project_id} 
                            className={`flex items-center gap-2 p-2 rounded ${isAssigned ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                          >
                            <Checkbox
                              id={`edit-project-${project.project_id}`}
                              checked={isAssigned}
                              onCheckedChange={() => toggleProjectAssignment(project.project_id, true)}
                            />
                            <label
                              htmlFor={`edit-project-${project.project_id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {project.name}
                            </label>
                            {isAssigned && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                                Assigned
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateUser} disabled={updating}>
                {updating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Company Edit Dialog */}
        <Dialog open={showCompanyEditDialog} onOpenChange={setShowCompanyEditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Company Profile</DialogTitle>
              <DialogDescription>
                Update your company information. Changes will be visible to all team members and administrators.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name *</Label>
                <Input
                  id="company-name"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="Enter company name"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-industry">Industry</Label>
                <Input
                  id="company-industry"
                  value={companyForm.industry}
                  onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                  placeholder="e.g., Healthcare, Technology"
                  data-testid="company-industry-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-website">Website</Label>
                <Input
                  id="company-website"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                  placeholder="e.g., www.example.com"
                  data-testid="company-website-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-phone">Phone</Label>
                <Input
                  id="company-phone"
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                  placeholder="e.g., (555) 123-4567"
                  data-testid="company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-address">Address</Label>
                <Input
                  id="company-address"
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                  placeholder="Enter company address"
                  data-testid="company-address-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompanyEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCompany} disabled={updatingCompany || !companyForm.name} data-testid="save-company-btn">
                {updatingCompany ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About Permission Levels</p>
                <ul className="space-y-1 text-blue-700">
                  <li><strong>Full Access:</strong> Can view all projects, time entries, invoices, and files</li>
                  <li><strong>Standard:</strong> Can view projects and complete assigned tasks</li>
                  <li><strong>Limited:</strong> Can only see projects they are specifically assigned to</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
};

export default ClientCompany;
